<template>
  <div class="ncrm-mitp-candidates-panel">
    <v-alert type="info" class="mb-4">
      <div class="text-subtitle-2">{{ $t("ncrm.integrations.appSyncs.candidates.title") }}</div>
      <div class="text-body-2">
        {{ $t("ncrm.integrations.appSyncs.candidates.description") }}
      </div>
    </v-alert>

    <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-4 app-toolbar-surface">
      <div class="d-flex flex-wrap align-center ga-3">
        <v-text-field
          v-model="search"
          :label="$t('ncrm.integrations.appSyncs.candidates.filters.search')"
          hide-details
          prepend-inner-icon="mdi-magnify"
          class="ncrm-mitp-candidates-panel__search"
        />
        <v-select
          v-model="statusFilter"
          :items="statusOptions"
          :label="$t('ncrm.integrations.appSyncs.candidates.filters.status')"
          hide-details
          class="ncrm-mitp-candidates-panel__filter"
        />
      </div>
      <div class="d-flex flex-wrap align-center ga-2">
        <v-btn variant="text" color="primary" :disabled="loading" @click="fetchCandidates">
          <v-icon start>mdi-refresh</v-icon>
          {{ $t("app.actions.refresh") }}
        </v-btn>
        <v-btn color="primary" variant="outlined" :disabled="!selectedCandidateIds.length" :loading="actionLoading" @click="handleCreateSelected">
          <v-icon start>mdi-domain-plus</v-icon>
          {{ $t("ncrm.integrations.appSyncs.candidates.actions.createSelected") }}
        </v-btn>
        <v-btn color="primary" :loading="actionLoading" @click="confirmCreateAll = true">
          <v-icon start>mdi-playlist-plus</v-icon>
          {{ $t("ncrm.integrations.appSyncs.candidates.actions.createAllEligible") }}
        </v-btn>
        <v-btn
          color="primary"
          variant="outlined"
          :disabled="!selectedCandidateIds.length"
          :loading="boardActionLoading"
          class="app-inline-action"
          @click="openBoardDialog(selectedCandidateIds)"
        >
          <v-icon start>mdi-view-kanban-outline</v-icon>
          {{ $t("ncrm.integrations.appSyncs.candidates.actions.addSelectedToBoard") }}
        </v-btn>
      </div>
    </div>

    <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-3">
      <div class="d-flex flex-wrap align-center ga-2">
        <v-chip
          v-for="summary in statusSummary"
          :key="summary.status"
          size="small"
          :color="summary.color"
          :variant="summary.variant"
        >
          {{ summary.label }}: {{ summary.count }}
        </v-chip>
      </div>
      <div v-if="selectedCandidateIds.length" class="text-caption text-medium-emphasis app-meta-surface ncrm-mitp-candidates-panel__selection">
        {{ $t("ncrm.integrations.appSyncs.candidates.selection", { count: selectedCandidateIds.length }) }}
      </div>
    </div>

    <v-card variant="outlined">
      <v-card-text class="pa-0">
        <v-data-table-server
          v-model="selectedCandidateIds"
          v-model:items-per-page="perPage"
          v-model:page="page"
          :headers="headers"
          :items="candidates"
          :items-length="total"
          :loading="loading"
          item-key="id"
          item-value="id"
          show-select
          class="elevation-0 ncrm-import-candidates__table"
        >
          <template #item.candidate_status="{ item }">
            <v-chip
              size="small"
              :color="resolveCandidateStatusMeta(item.candidate_status).color"
              :variant="resolveCandidateStatusMeta(item.candidate_status).variant"
            >
              {{ translateCandidateStatus(item.candidate_status) }}
            </v-chip>
          </template>
          <template #item.lead_board_status="{ item }">
            <v-chip
              v-if="item.ncrm_company_id"
              size="small"
              :color="resolveCandidateBoardStatusMeta(item.lead_board_status).color"
              :variant="resolveCandidateBoardStatusMeta(item.lead_board_status).variant"
            >
              {{ translateCandidateBoardStatus(item.lead_board_status) }}
            </v-chip>
            <span v-else>-</span>
          </template>
          <template #item.recall_date="{ item }">
            {{ formatDate(item.recall_date) }}
          </template>
          <template #item.ncrm_company_name="{ item }">
            <v-btn
              v-if="item.ncrm_company_id"
              size="small"
              variant="text"
              color="primary"
              class="app-entity-link"
              @click="openMatchedCompany(item)"
            >
              <v-icon start size="16">mdi-domain</v-icon>
              {{ item.ncrm_company_name || $t("ncrm.integrations.appSyncs.items.openMatchedCompany") }}
            </v-btn>
            <span v-else>-</span>
          </template>
          <template #item.last_seen_at="{ item }">
            {{ formatDateTime(item.last_seen_at) }}
          </template>
          <template #item.actions="{ item }">
            <div class="d-flex flex-column align-start">
              <v-btn
                v-if="item.candidate_status === 'eligible'"
                size="small"
                variant="text"
                color="primary"
                class="app-inline-action"
                @click="handleCreateSingle(item)"
              >
                <v-icon start size="16">mdi-domain-plus</v-icon>
                {{ $t("ncrm.integrations.appSyncs.candidates.actions.createCompany") }}
              </v-btn>
              <v-btn
                v-else-if="item.candidate_status === 'needs_review' && item.latest_job_id && item.latest_job_item_id"
                size="small"
                variant="tonal"
                color="secondary"
                class="app-inline-action app-inline-action--secondary"
                @click="openResolveDialog(item)"
              >
                <v-icon start size="16">mdi-link-variant</v-icon>
                {{ $t("ncrm.integrations.appSyncs.candidates.actions.review") }}
              </v-btn>
              <v-btn
                v-if="item.ncrm_company_id"
                size="small"
                variant="text"
                color="primary"
                class="app-inline-action"
                @click="openBoardDialog([item.id])"
              >
                <v-icon start size="16">mdi-view-kanban-outline</v-icon>
                {{ $t("ncrm.integrations.appSyncs.candidates.actions.addToBoard") }}
              </v-btn>
              <v-btn
                v-if="item.detail_url"
                :href="item.detail_url"
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                variant="text"
                color="primary"
                class="app-entity-link"
              >
                <v-icon start size="16">mdi-open-in-new</v-icon>
                {{ $t("ncrm.integrations.appSyncs.items.openSource") }}
              </v-btn>
            </div>
          </template>
          <template #no-data>
            <v-alert type="info" class="ma-4">
              {{ $t("ncrm.integrations.appSyncs.candidates.empty") }}
            </v-alert>
          </template>
        </v-data-table-server>
      </v-card-text>
    </v-card>

    <NcrmMitpResolveUnmatchedDialog
      v-model="resolveDialogOpen"
      :job-id="resolveJobId"
      :item="resolveItem"
      @error="showSnackbar($event, 'error')"
      @resolved="handleResolveCompleted"
    />

    <NcrmMitpLeadBoardDialog
      v-model="boardDialogOpen"
      :candidates-count="boardCandidateIds.length"
      :submitting="boardActionLoading"
      @confirm="handleBoardConfirm"
      @error="showSnackbar($event, 'error')"
    />

    <v-dialog :model-value="confirmCreateAll" max-width="520" @update:model-value="confirmCreateAll = Boolean($event)">
      <v-card>
        <v-card-title class="text-h6">
          {{ $t("ncrm.integrations.appSyncs.candidates.confirmCreateAll.title") }}
        </v-card-title>
        <v-card-text class="pt-2">
          {{ $t("ncrm.integrations.appSyncs.candidates.confirmCreateAll.description") }}
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-btn variant="text" @click="confirmCreateAll = false">
            {{ $t("app.actions.cancel") }}
          </v-btn>
          <v-spacer />
          <v-btn color="primary" :loading="actionLoading" @click="handleCreateAllEligible">
            {{ $t("ncrm.integrations.appSyncs.candidates.confirmCreateAll.submit") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :timeout="3200" :color="snackbar.color">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import axios from "axios";
import NcrmMitpLeadBoardDialog from "@/components/ncrm/integrations/NcrmMitpLeadBoardDialog.vue";
import NcrmMitpResolveUnmatchedDialog from "@/components/ncrm/integrations/NcrmMitpResolveUnmatchedDialog.vue";
import {
  formatMitpStatusLabel,
  MITP_CANDIDATE_FILTERS,
  resolveMitpCandidateBoardStatusMeta,
  resolveMitpCandidateStatusMeta,
} from "@/Utile/ncrmMitpSync.mjs";

export default {
  name: "NcrmMitpImportCandidatesPanel",
  components: {
    NcrmMitpLeadBoardDialog,
    NcrmMitpResolveUnmatchedDialog,
  },
  data() {
    return {
      actionLoading: false,
      boardActionLoading: false,
      boardCandidateIds: [],
      boardDialogOpen: false,
      candidates: [],
      confirmCreateAll: false,
      loading: false,
      page: 1,
      perPage: 25,
      resolveDialogOpen: false,
      resolveItem: null,
      resolveJobId: null,
      search: "",
      selectedCandidateIds: [],
      snackbar: {
        show: false,
        message: "",
        color: "info",
      },
      statusFilter: "open",
      total: 0,
    };
  },
  computed: {
    headers() {
      return [
        { title: this.$t("ncrm.integrations.appSyncs.candidates.headers.company"), key: "mitp_company_name" },
        { title: this.$t("ncrm.integrations.appSyncs.candidates.headers.idno"), key: "mitp_idno" },
        { title: this.$t("ncrm.integrations.appSyncs.candidates.headers.status"), key: "candidate_status" },
        { title: this.$t("ncrm.integrations.appSyncs.candidates.headers.resident"), key: "mitp_resident_status" },
        { title: this.$t("ncrm.integrations.appSyncs.candidates.headers.recallDate"), key: "recall_date" },
        { title: this.$t("ncrm.integrations.appSyncs.candidates.headers.crmCompany"), key: "ncrm_company_name" },
        { title: this.$t("ncrm.integrations.appSyncs.candidates.headers.leadBoard"), key: "lead_board_status" },
        { title: this.$t("ncrm.integrations.appSyncs.candidates.headers.lastSeen"), key: "last_seen_at" },
        { title: this.$t("ncrm.integrations.appSyncs.candidates.headers.actions"), key: "actions", sortable: false },
      ];
    },
    statusOptions() {
      return MITP_CANDIDATE_FILTERS.map((value) => ({
        title: this.translateCandidateFilter(value),
        value,
      }));
    },
    statusSummary() {
      const trackedStatuses = ["needs_review", "eligible", "created", "linked_existing"];
      const counts = new Map();
      trackedStatuses.forEach((status) => counts.set(status, 0));
      this.candidates.forEach((candidate) => {
        const normalized = String(candidate?.candidate_status || "").trim().toLowerCase();
        if (!counts.has(normalized)) return;
        counts.set(normalized, counts.get(normalized) + 1);
      });
      return trackedStatuses
        .map((status) => {
          const count = counts.get(status) || 0;
          if (!count) return null;
          const meta = this.resolveCandidateStatusMeta(status);
          return {
            status,
            count,
            color: meta.color,
            variant: meta.variant,
            label: this.translateCandidateStatus(status),
          };
        })
        .filter(Boolean);
    },
  },
  watch: {
    page() {
      this.fetchCandidates();
    },
    perPage() {
      this.page = 1;
      this.fetchCandidates();
    },
    search() {
      this.page = 1;
      this.fetchCandidates();
    },
    statusFilter() {
      this.page = 1;
      this.fetchCandidates();
    },
  },
  mounted() {
    this.fetchCandidates();
  },
  methods: {
    resolveCandidateStatusMeta(status) {
      return resolveMitpCandidateStatusMeta(status);
    },
    resolveCandidateBoardStatusMeta(status) {
      return resolveMitpCandidateBoardStatusMeta(status);
    },
    translateCandidateStatus(status) {
      const normalized = String(status || "").trim().toLowerCase();
      const translated = this.$t(`ncrm.integrations.appSyncs.candidates.status.${normalized}`);
      return translated === `ncrm.integrations.appSyncs.candidates.status.${normalized}`
        ? formatMitpStatusLabel(status)
        : translated;
    },
    translateCandidateBoardStatus(status) {
      const normalized = String(status || "").trim().toLowerCase();
      const translated = this.$t(`ncrm.integrations.appSyncs.candidates.boardStatus.${normalized}`);
      return translated === `ncrm.integrations.appSyncs.candidates.boardStatus.${normalized}`
        ? formatMitpStatusLabel(status)
        : translated;
    },
    translateCandidateFilter(value) {
      const translated = this.$t(`ncrm.integrations.appSyncs.candidates.filters.options.${value}`);
      return translated === `ncrm.integrations.appSyncs.candidates.filters.options.${value}`
        ? formatMitpStatusLabel(value)
        : translated;
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(`${value}T00:00:00`);
      if (Number.isNaN(date.getTime())) return String(value);
      return [
        String(date.getDate()).padStart(2, "0"),
        String(date.getMonth() + 1).padStart(2, "0"),
        date.getFullYear(),
      ].join(".");
    },
    formatDateTime(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      const datePart = [
        String(date.getDate()).padStart(2, "0"),
        String(date.getMonth() + 1).padStart(2, "0"),
        date.getFullYear(),
      ].join(".");
      const timePart = `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
      return `${datePart}, ${timePart}`;
    },
    showSnackbar(message, color = "info") {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    openMatchedCompany(item) {
      const companyId = Number(item?.ncrm_company_id || 0);
      if (companyId) this.$router.push(`/ncrm/companies/${companyId}`);
    },
    openResolveDialog(candidate) {
      this.resolveJobId = Number(candidate?.latest_job_id || 0) || null;
      this.resolveItem = candidate?.latest_job_item_id
        ? {
            id: Number(candidate.latest_job_item_id),
            mitp_company_name: candidate.mitp_company_name,
            mitp_idno: candidate.mitp_idno,
            detail_url: candidate.detail_url,
          }
        : null;
      this.resolveDialogOpen = Boolean(this.resolveJobId && this.resolveItem?.id);
    },
    openBoardDialog(candidateIds) {
      this.boardCandidateIds = [...new Set((candidateIds || []).map((id) => Number(id || 0)).filter(Boolean))];
      if (!this.boardCandidateIds.length) return;
      this.boardDialogOpen = true;
    },
    async fetchCandidates() {
      this.loading = true;
      try {
        const { data } = await axios.get("/ncrm/integrations/mitp/candidates", {
          params: {
            page: this.page,
            perPage: this.perPage,
            status: this.statusFilter || undefined,
            search: this.search || undefined,
          },
        });
        this.candidates = Array.isArray(data?.items) ? data.items : [];
        this.total = Number(data?.total || this.candidates.length || 0);
        const visibleIds = new Set(this.candidates.map((item) => Number(item.id)));
        this.selectedCandidateIds = this.selectedCandidateIds.filter((id) => visibleIds.has(Number(id)));
      } catch (error) {
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.integrations.appSyncs.candidates.messages.loadCandidatesError"),
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
    buildImportSummaryMessage(summary = {}) {
      return this.$t("ncrm.integrations.appSyncs.candidates.messages.importSummary", {
        created: Number(summary?.created_count || 0),
        linked: Number(summary?.linked_existing_count || 0),
        review: Number(summary?.needs_review_count || 0),
        errors: Number(summary?.error_count || 0),
      });
    },
    buildBoardSummaryMessage(summary = {}) {
      return this.$t("ncrm.integrations.appSyncs.candidates.messages.boardSummary", {
        added: Number(summary?.added_count || 0),
        already: Number(summary?.already_in_board_count || 0),
        errors: Number(summary?.error_count || 0),
      });
    },
    async handleCreateRequest(payload) {
      this.actionLoading = true;
      try {
        const { data } = await axios.post("/ncrm/integrations/mitp/candidates/import", payload);
        await this.fetchCandidates();
        this.selectedCandidateIds = [];
        this.showSnackbar(this.buildImportSummaryMessage(data?.summary), "success");
      } catch (error) {
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.integrations.appSyncs.candidates.messages.importError"),
          "error"
        );
      } finally {
        this.actionLoading = false;
      }
    },
    async handleCreateSelected() {
      if (!this.selectedCandidateIds.length) return;
      await this.handleCreateRequest({
        mode: "selected",
        candidate_ids: this.selectedCandidateIds,
      });
    },
    async handleCreateSingle(candidate) {
      await this.handleCreateRequest({
        mode: "selected",
        candidate_ids: [candidate?.id],
      });
    },
    async handleCreateAllEligible() {
      this.confirmCreateAll = false;
      await this.handleCreateRequest({
        mode: "all_eligible",
      });
    },
    async handleBoardConfirm({ boardId, stageId }) {
      this.boardActionLoading = true;
      try {
        const { data } = await axios.post("/ncrm/integrations/mitp/candidates/add-to-board", {
          candidate_ids: this.boardCandidateIds,
          board_id: boardId,
          stage_id: stageId,
        });
        this.boardDialogOpen = false;
        this.boardCandidateIds = [];
        await this.fetchCandidates();
        this.showSnackbar(this.buildBoardSummaryMessage(data?.summary), "success");
      } catch (error) {
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.integrations.appSyncs.candidates.messages.boardError"),
          "error"
        );
      } finally {
        this.boardActionLoading = false;
      }
    },
    async handleResolveCompleted() {
      await this.fetchCandidates();
      this.showSnackbar(this.$t("ncrm.integrations.appSyncs.resolve.success"), "success");
    },
  },
};
</script>

<style scoped>
.ncrm-mitp-candidates-panel__filter {
  flex: 0 0 260px;
  min-width: 260px;
  width: 260px;
}

.ncrm-mitp-candidates-panel__search {
  width: min(100%, 320px);
}

.ncrm-mitp-candidates-panel__selection {
  padding: 8px 12px;
}

@media (max-width: 600px) {
  .ncrm-mitp-candidates-panel__filter,
  .ncrm-mitp-candidates-panel__search {
    flex: 1 1 100%;
    min-width: 0;
    width: 100%;
  }
}
</style>
